import type { AdHocFilter } from "@domains/segments/shared/segmentsTypes.ts";
import { useScopeSegmentsSection, type ScopeSegmentsFooterContext } from "@domains/segments/client/components/scope/useScopeSegmentsSection.tsx";
import { Button } from "@/components/ui/button.tsx";
import { PlusIcon } from "@heroicons/react/20/solid";
import { AdjustmentsHorizontalIcon } from "@heroicons/react/24/outline";
import type { ScopeSelectorSegmentsSectionSlots } from "@packages/core-client/scope/ScopeSelector.tsx";
import { useFeatureFlag } from "@squide/firefly";
import { useI18nextInstance } from "@squide/i18next";
import { useMixpanelClient } from "@workleap/telemetry/react";
import { useCallback, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ManageSegmentsModal } from "../components/manage-segments/ManageSegmentsModal.tsx";
import { i18NextInstanceKey } from "../i18n/i18next.ts";

export interface UseScopeSegmentsOptions {
    segmentId: string | null;
    adHocFilter: AdHocFilter | null;
    onAddFilter?: () => void;
}

export interface ScopeSegments {
    isEnabled: boolean;
    activeSegmentName: string | undefined;
    render: (context: { onRequestClose: () => void }) => ScopeSelectorSegmentsSectionSlots;
    onPopoverOpenChange: (isOpen: boolean) => void;
    persistentChrome: ReactNode;
}

export function useScopeSegments({ segmentId, adHocFilter, onAddFilter }: UseScopeSegmentsOptions): ScopeSegments {
    const isEnabled = useFeatureFlag("enable-team-segments", false);
    const i18nextInstance = useI18nextInstance(i18NextInstanceKey);
    const { t } = useTranslation("ScopeSegmentsSection", { i18n: i18nextInstance });
    const [manageSegmentsPanel, setManageSegmentsPanel] = useState<string | null>(null);
    const mixpanelClient = useMixpanelClient({ throwOnUndefined: false });
    const track = useMemo(() => mixpanelClient?.createTrackingFunction(), [mixpanelClient]);

    const handleManageSegments = useCallback(
        ({ onRequestClose, isSegmentActive }: ScopeSegmentsFooterContext) => {
            onRequestClose();
            setManageSegmentsPanel(adHocFilter ? "current-filters" : isSegmentActive && segmentId ? segmentId : "new");
            void track?.("Manage Segments Opened", { Source: "Team scope selector" });
        },
        [adHocFilter, segmentId, track]
    );

    const handleAddFilter = useCallback(
        (onRequestClose: () => void) => {
            onRequestClose();
            onAddFilter?.();
        },
        [onAddFilter]
    );

    const renderFooterActions = useCallback(
        (context: ScopeSegmentsFooterContext) => (
            <div className="flex flex-col p-2">
                {onAddFilter && (
                    <Button intent="plain" onPress={() => handleAddFilter(context.onRequestClose)} isDisabled={context.isHandPickedSegmentActive} className="w-full justify-start">
                        <PlusIcon aria-hidden="true" />
                        {t("addFilter")}
                    </Button>
                )}
                <Button intent="plain" onPress={() => handleManageSegments(context)} className="w-full justify-start">
                    <AdjustmentsHorizontalIcon aria-hidden="true" />
                    {t("manageSegments")}
                </Button>
            </div>
        ),
        [onAddFilter, handleAddFilter, handleManageSegments, t]
    );

    const { activeSegmentName, render, onPopoverOpenChange, liveRegion } = useScopeSegmentsSection({ isEnabled, segmentId, renderFooterActions });

    const persistentChrome = useMemo(
        () => (
            <>
                {liveRegion}
                <ManageSegmentsModal panel={manageSegmentsPanel} onPanelChange={setManageSegmentsPanel} />
            </>
        ),
        [liveRegion, manageSegmentsPanel]
    );

    return {
        isEnabled,
        activeSegmentName,
        render,
        onPopoverOpenChange,
        persistentChrome
    };
}
